/**
 * In-app signup screen. Reads referral context from `pendingReferral`
 * (stored on device when a deep-link landed earlier), resolves it
 * server-side to inviter + deal, then hands the data to <SignupCard>.
 *
 * Routing here (vs `/(auth)/login`) is decided by `app/index.tsx`'s
 * 3-condition router. This screen doesn't decide which mode to render
 * itself — it asks the server (`referral_invites`) for inviter + deal
 * info; whatever resolves is what we display.
 */
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { BrandedAuthShell } from '@/components/BrandedAuthShell';
import { SignupCard } from '@/components/SignupCard';
import { getPendingReferral } from '@/lib/pendingReferral';
import { publicSignupService, type ReferralInviteResponse } from '@/services/publicSignup';

const SignupScreen = observer(() => {
  const [referralToken, setReferralToken] = useState<string | undefined>(undefined);
  const [refId, setRefId] = useState<string | undefined>(undefined);
  const [invite, setInvite] = useState<ReferralInviteResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const referral = await getPendingReferral().catch(() => null);
      if (cancelled) return;
      const token = referral?.referralLinkToken;
      const ref = referral?.refId;
      setReferralToken(token);
      setRefId(ref);

      if (token) {
        try {
          const data = await publicSignupService.invite(token);
          if (!cancelled) setInvite(data);
        } catch {
          // No-op: degrade to generic mode.
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <BrandedAuthShell>
      <SignupCard
        deal={invite?.deal ?? null}
        inviter={invite?.inviter ?? null}
        referralToken={referralToken}
        refId={refId}
        loading={loading}
      />
    </BrandedAuthShell>
  );
});

export default SignupScreen;
